import axios from 'axios';
import API_CONFIG from '../config/api';

class ApiClient {
  constructor() {
    this.client = axios.create({
      baseURL: API_CONFIG.BASE_URL,
      timeout: API_CONFIG.TIMEOUT,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  setupInterceptors() {
    // Request interceptor - add auth token
    this.client.interceptors.request.use(
      (config) => {
        const token = this.getToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        
        // Add request timestamp for debugging
        config.metadata = { requestStartedAt: new Date().getTime() };
        
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor - handle errors and token refresh
    this.client.interceptors.response.use(
      (response) => {
        // Add response time to metadata
        if (response.config.metadata) {
          response.config.metadata.responseTime = 
            new Date().getTime() - response.config.metadata.requestStartedAt;
        }
        
        return response;
      },
      async (error) => {
        const originalRequest = error.config;

        // Handle rate limiting (429)
        if (error.response?.status === 429) {
          const rateLimitError = new Error(
            error.response.data?.message ||
            'Too many requests. Please wait a moment and try again.'
          );
          rateLimitError.isRateLimit = true;
          rateLimitError.status = 429;
          rateLimitError.data = error.response.data;
          return Promise.reject(rateLimitError);
        }

        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;

          try {
            const refreshToken = this.getRefreshToken();
            if (refreshToken) {
              const response = await this.refreshAccessToken(refreshToken);
              const { accessToken } = response.data.data;

              this.setToken(accessToken);
              originalRequest.headers.Authorization = `Bearer ${accessToken}`;

              return this.client(originalRequest);
            }
          } catch (refreshError) {
            this.clearTokens();
            window.dispatchEvent(new CustomEvent('auth:logout'));
            return Promise.reject(refreshError);
          }
        }

        // Handle subscription required (403 with NO_ACTIVE_SUBSCRIPTION)
        if (error.response?.status === 403 && error.response?.data?.code === 'NO_ACTIVE_SUBSCRIPTION') {
          const subError = new Error(error.response.data.message || 'Active subscription required');
          subError.isSubscriptionRequired = true;
          subError.status = 403;
          return Promise.reject(subError);
        }

        // Handle network errors
        if (!error.response) {
          error.message = 'Network error. Please check your connection.';
        }

        return Promise.reject(error);
      }
    );
  }

  // Token management
  getToken() {
    return localStorage.getItem('accessToken');
  }

  setToken(token) {
    localStorage.setItem('accessToken', token);
  }

  getRefreshToken() {
    return localStorage.getItem('refreshToken');
  }

  setRefreshToken(token) {
    localStorage.setItem('refreshToken', token);
  }

  clearTokens() {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
  }

  // Auth methods
  async refreshAccessToken(refreshToken) {
    // Don't use interceptors for refresh request to avoid infinite loop
    const refreshClient = axios.create({
      baseURL: API_CONFIG.BASE_URL,
      timeout: API_CONFIG.TIMEOUT,
    });

    return refreshClient.post('/api/auth/refresh', { refreshToken });
  }

  // HTTP methods with error handling and retries
  async request(config, retries = API_CONFIG.RETRY_ATTEMPTS) {
    try {
      const response = await this.client.request(config);
      return response.data;
    } catch (error) {
      if (retries > 0 && this.shouldRetry(error)) {
        await this.delay(API_CONFIG.RETRY_DELAY);
        return this.request(config, retries - 1);
      }
      throw this.handleError(error);
    }
  }

  async get(url, config = {}) {
    return this.request({ ...config, method: 'get', url });
  }

  async post(url, data, config = {}) {
    return this.request({ ...config, method: 'post', url, data });
  }

  async put(url, data, config = {}) {
    return this.request({ ...config, method: 'put', url, data });
  }

  async patch(url, data, config = {}) {
    return this.request({ ...config, method: 'patch', url, data });
  }

  async delete(url, config = {}) {
    return this.request({ ...config, method: 'delete', url });
  }

  // Utility methods
  shouldRetry(error) {
    // Retry on network errors or 5xx server errors
    return !error.response || (error.response.status >= 500);
  }

  delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  handleError(error) {
    const errorInfo = {
      message: error.message,
      status: error.response?.status,
      data: error.response?.data,
      url: error.config?.url,
      method: error.config?.method,
    };

    // Return user-friendly error with details preserved
    if (error.response?.data?.message || error.response?.data?.error) {
      const err = new Error(error.response.data.message || error.response.data.error);
      err.details = error.response?.data?.details;
      err.status = error.response?.status;
      return err;
    } else if (error.response?.status === 404) {
      return new Error('Resource not found');
    } else if (error.response?.status === 403) {
      return new Error('Access denied');
    } else if (error.response?.status >= 500) {
      return new Error('Server error. Please try again later.');
    }

    return error;
  }

  // File upload helper
  async uploadFile(url, file, onUploadProgress) {
    const formData = new FormData();
    formData.append('file', file);

    return this.request({
      method: 'post',
      url,
      data: formData,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress,
    });
  }

  // Health check
  async healthCheck() {
    try {
      const response = await this.get('/health');
      return response.status === 'OK';
    } catch (error) {
      return false;
    }
  }

  // Cancel all pending requests
  cancelAllRequests() {
    // This would require implementing a cancellation token system
    // For now, we'll just create a new client instance
    this.client = axios.create({
      baseURL: API_CONFIG.BASE_URL,
      timeout: API_CONFIG.TIMEOUT,
      headers: {
        'Content-Type': 'application/json',
      },
    });
    this.setupInterceptors();
  }
}

// Create singleton instance
const apiClient = new ApiClient();

export default apiClient;